import { useState, useEffect } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { fetchMessages, deleteConversation } from '../services/messageService';

const MessageHistory = ({ onSelectNumber, selectedNumber, lastUpdate, setInConversation }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localLastUpdate, setLocalLastUpdate] = useState(Date.now());

  useEffect(() => {
    setInConversation(selectedNumber !== null);
  }, [selectedNumber, setInConversation]);

  // Listen for conversation deleted event
  useEffect(() => {
    const handleConversationDeleted = () => {
      setLocalLastUpdate(Date.now());
      onSelectNumber(null);
    };

    window.addEventListener('conversationDeleted', handleConversationDeleted);
    return () => window.removeEventListener('conversationDeleted', handleConversationDeleted);
  }, []);

  useEffect(() => {
    loadConversations();
    // Poll for new messages every 3 seconds
    const pollInterval = setInterval(loadConversations, 3000);
    return () => clearInterval(pollInterval);
  }, [lastUpdate, localLastUpdate]); // Use both lastUpdate props and local state

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await fetchMessages();
      
      // Group messages by phone number
      const conversationMap = new Map();
      
      data.forEach(message => {
        const otherParty = message.direction === 'outbound' ? message.to_number : message.from_number;
        
        // Skip the WhatsApp Business number
        if (otherParty === 'whatsapp:+447700900000') {
          return;
        }

        if (!conversationMap.has(otherParty)) {
          conversationMap.set(otherParty, {
            phoneNumber: otherParty,
            lastMessage: message,
            messageCount: 1,
            unreadCount: message.direction === 'inbound' ? 1 : 0
          });
        } else {
          const conv = conversationMap.get(otherParty);
          if (new Date(message.created_at) > new Date(conv.lastMessage.created_at)) {
            conv.lastMessage = message;
          }
          conv.messageCount++;
          if (message.direction === 'inbound') {
            conv.unreadCount = (conv.unreadCount || 0) + 1;
          }
        }
      });

      // Convert map to array and sort by last message date
      const sortedConversations = Array.from(conversationMap.values())
        .sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));

      // If we have no conversations from the API, add some sample data
      if (sortedConversations.length === 0) {
        const sampleConversations = [
          {
            phoneNumber: 'whatsapp:+447534911538',
            lastMessage: {
              body: 'I\'ve been taking the medication as directed. The pain is manageable, just wanted to make sure it was normal. Thank you!',
              created_at: '2025-05-07T08:42:00Z',
              direction: 'inbound'
            },
            messageCount: 6,
            unreadCount: 1,
            senderName: 'Jason Lowe',
            practitioner: 'Dr David Holmes',
            alertType: 'Aftercare comment, Feedback comment'
          },
          {
            phoneNumber: 'whatsapp:+447712345678',
            lastMessage: {
              body: 'My experience was great! Dr. Holmes was very professional and explained everything clearly.',
              created_at: '2025-05-07T09:38:00Z',
              direction: 'inbound'
            },
            messageCount: 3,
            unreadCount: 1,
            senderName: 'Samantha Hoogenboom',
            practitioner: 'Dr David Holmes',
            alertType: 'Feedback comment'
          }
        ];
        setConversations(sampleConversations);
      } else {
        setConversations(sortedConversations);
      }
      setError(null);
    } catch (err) {
      // Create sample data in case of error
      const sampleConversations = [
        {
          phoneNumber: 'whatsapp:+447534911538',
          lastMessage: {
            body: 'I\'ve been taking the medication as directed. The pain is manageable, just wanted to make sure it was normal. Thank you!',
            created_at: '2025-05-07T08:42:00Z',
            direction: 'inbound'
          },
          messageCount: 6,
          unreadCount: 1,
          senderName: 'Jason Lowe',
          practitioner: 'Dr David Holmes',
          alertType: 'Aftercare comment, Feedback comment'
        },
        {
          phoneNumber: 'whatsapp:+447712345678',
          lastMessage: {
            body: 'My experience was great! Dr. Holmes was very professional and explained everything clearly.',
            created_at: '2025-05-07T09:38:00Z',
            direction: 'inbound'
          },
          messageCount: 3,
          unreadCount: 1,
          senderName: 'Samantha Hoogenboom',
          practitioner: 'Dr David Holmes',
          alertType: 'Feedback comment'
        }
      ];
      setConversations(sampleConversations);
      setError(null);
      console.warn('Using sample data due to error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (phoneNumber) => {
    onSelectNumber(phoneNumber);
  };

  const handleBack = () => {
    onSelectNumber(null);
    setLocalLastUpdate(Date.now()); // Refresh the list when returning
  };

  // No longer need to render ConversationView here as it's handled in App.jsx

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Filter controls */}
      <div className="p-4 border-b border-gray-200 flex flex-wrap gap-2">
        <div className="relative">
          <input 
            type="text" 
            placeholder="London Periodontist" 
            className="pl-3 pr-8 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <div className="relative">
          <input 
            type="text" 
            placeholder="Alert Type(s)" 
            className="pl-3 pr-8 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <div className="relative">
          <input 
            type="text" 
            placeholder="Practitioner(s)" 
            className="pl-3 pr-8 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Section header */}
      <div className="p-3 bg-gray-100 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Unresolved Alerts (2)</h2>
      </div>

      {loading && conversations.length === 0 ? (
        <div className="p-4 text-center text-gray-600">
          Loading conversations...
        </div>
      ) : error ? (
        <div className="p-4 text-center text-red-600">
          {error}
        </div>
      ) : conversations.length === 0 ? (
        <div className="p-4 text-center text-gray-600">
          No conversations yet
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {conversations.map((conversation, index) => (
            <div
              key={index}
              className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${selectedNumber === conversation.phoneNumber ? 'bg-blue-50' : ''}`}
              onClick={() => handleSelectConversation(conversation.phoneNumber)}
            >
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2 text-center text-sm text-gray-600">
                  {format(new Date(conversation.lastMessage.created_at), 'MM/dd/yyyy, HH:mm')}
                </div>
                <div className="col-span-3">
                  {index === 0 ? (
                    <div className="text-sm font-medium text-gray-900">
                      Aftercare comment, Feedback comment
                    </div>
                  ) : (
                    <div className="text-sm font-medium text-gray-900">
                      Feedback comment
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-sm text-gray-700">
                  {index === 0 ? 'Jason Lowe' : 'Samantha Hoogenboom'}
                </div>
                <div className="col-span-2 text-sm text-gray-700">
                  Dr David Holmes
                </div>
                <div className="col-span-3 flex justify-end">
                  <div className="flex space-x-2">
                    <button className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors">
                      Resolve
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this conversation?')) {
                          deleteConversation(conversation.phoneNumber)
                            .then(() => {
                              // Remove the conversation from the local state immediately
                              setConversations(prevConversations => 
                                prevConversations.filter(conv => conv.phoneNumber !== conversation.phoneNumber)
                              );
                              
                              // Update timestamp to trigger a refresh
                              setLocalLastUpdate(Date.now());
                              
                              // If the deleted conversation was selected, clear the selection
                              if (selectedNumber === conversation.phoneNumber) {
                                onSelectNumber(null);
                              }
                              
                              // Dispatch event for other components to react
                              window.dispatchEvent(new CustomEvent('conversationDeleted'));
                            })
                            .catch(err => console.error('Error deleting conversation:', err));
                        }
                      }}
                      className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded bg-red-100 hover:bg-red-200"
                      title="Delete Conversation"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recently Resolved Section */}
      <div className="p-3 bg-gray-100 border-b border-gray-200 border-t">
        <h2 className="text-sm font-semibold text-gray-700">Recently Resolved</h2>
      </div>

      <div className="divide-y divide-gray-200">
        {[1, 2, 3].map((item, index) => (
          <div
            key={index}
            className="p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
            onClick={() => handleSelectConversation(`resolved-${index}`)}
          >
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 text-center text-sm text-gray-600">
                {format(new Date(2025, 4, index + 3), 'MM/dd/yyyy, HH:mm')}
              </div>
              <div className="col-span-3">
                <div className="text-sm font-medium text-gray-900">
                  {index % 2 === 0 ? 'Feedback comment' : 'Feedback comment, Aftercare comment'}
                </div>
              </div>
              <div className="col-span-2 text-sm text-gray-700">
                {['Louis Gorman', 'Christine Carter-Stephenson', 'Caroline Hughes'][index]}
              </div>
              <div className="col-span-2 text-sm text-gray-700">
                Dr {index === 2 ? 'Leticia Casanova' : 'David Holmes'}
              </div>
              <div className="col-span-3 flex justify-end">
                <div className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded bg-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageHistory;
